import "./styles.scss";
import { useUserContext } from "../../hooks/userContext";
import { ApplicationStatusBlock } from "./application-status-block";
import { WelcomeMessage } from "./welcome-message";
import { DesignerWelcomePopup } from "./designer-welcome-popup";
import { DesignerApplicationSteps } from "../../__generated__/types";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Pages } from "../../utils";
import { YourHubView } from "../your-hub-page";
import { AdminDashboardPage } from "../admin-dashboard-page";
import { isAdminUser } from "../../utils";
import { PaymentGate } from "../../components/payment-gate";
import { PlanKey } from "../registration/homeowner-plan-selection-fieldset";
import { useNotification } from "../../contexts/NotificationContext";
import { BasicPlanDashboard } from "./basic-plan-dashboard";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import { usePaymentSettings } from "../../contexts/PaymentSettingsContext";
import { Box, Skeleton } from "@mui/material";
import { useHomeownerOnboardingAccess } from "../../hooks/useHomeownerOnboardingAccess";

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

const DESIGNER_ONBOARDING_WELCOME_MESSAGE =
  "Once you apply, the membership process takes roughly a week to complete. Log into your Designer Hub at any time to check your application progress.";

export const DashboardPage = () => {
  const { user, loading } = useUserContext();
  const { effectiveRole, effectiveTier, isAdmin, isDesigner, isHomeowner } = useEffectiveRole();
  const { isPaymentEnabled, isTestMode } = usePaymentSettings();
  const { showSuccess } = useNotification();

  

  const ctx = useDesignerApplicationContext();
  const { application, hasApplicationDataLoaded } = ctx as any;

  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const navigate = useNavigate();
  const homeownerOnboardingAccess = useHomeownerOnboardingAccess();

  // Compute redirect path synchronously during render to prevent flash
  // This runs before any rendering happens
  const computeRedirectPath = (): string | null => {
    // Wait for user data to load
    if (loading) {
      return null; // Still loading, show skeleton
    }

    // Admin users stay on dashboard
    if (isAdmin) {
      return null;
    }
    
    // No user logged in, redirect to login
    if (!user) {
      return Pages.LOGIN;
    }

    // Enforce email verification (emailVerified can be string or boolean depending on source)
    const emailVerifiedFlag = getEmailVerifiedFlag(user.emailVerified);
    if (emailVerifiedFlag !== true) {
      const userTypeParam = user.accountType || "Designer";
      return `${Pages.SIGNUP}?userType=${userTypeParam}&step=2&resume=true`;
    }

    // Check if account is incomplete
    const isBetaMode = !isPaymentEnabled() || isTestMode();
    const isDesignerAccount = user.accountType === 'Designer';
    const canAccessDashboard = isDesignerAccount ||
      user.onboardingStatus === 'active' ||
      (isBetaMode && (user.onboardingStatus === 'pending_profile_completion' || user.onboardingStatus === 'profile_completed') && user.accountType === 'Designer');
    
    if (!canAccessDashboard) {
      // Redirect to registration page to complete setup (homeowners only)
      const userTypeParam = user.accountType || 'Homeowner';
      return `${Pages.SIGNUP}?userType=${userTypeParam}`;
    }

    // For designers, redirect to your-hub:
    // 1. If onboarding is incomplete (pending_profile_completion)
    // 2. If onboarding is complete (active) - designers should use your-hub as their main dashboard
    if (user.accountType === 'Designer') {
      if (user.onboardingStatus === 'pending_profile_completion') {
        return '/your-hub';
      }
      
      if (user.onboardingStatus === 'active') {
        return '/your-hub';
      }
    }

    // For homeowners, redirect to your-hub for onboarding check
    // Your-hub will determine if onboarding is complete and show appropriate content
    // Homeowners must complete: Course, Matching Profile, and First Project
    if (user.accountType === 'Homeowner') {
      return '/your-hub';
    }

    // If we get here, user should stay on dashboard
    return null;
  };

  const redirectPath = computeRedirectPath();

  // Get user's subscription plan and map GraphQL enum values to PlanKey
  // Handle both object format { value: "GuildPlus" } and string format "GuildPlus"
  const rawSubscription = typeof user?.subscription === 'string' 
    ? user.subscription 
    : (user?.subscription as any)?.homeownerValue ||
      (user?.subscription as any)?.designerValue ||
      (user?.subscription as any)?.value ||
      (user?.subscription as any)?.tier ||
      'basic';
  
  // Map GraphQL enum values to PlanKey type
  const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
    const normalized = String(sub ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

    if (!normalized || normalized === "free" || normalized === "basic") {
      return "basic";
    }
    if (
      normalized === "guildannually" ||
      normalized === "guildplus" ||
      normalized === "guildplusbeta" ||
      normalized === "guild+"
    ) {
      return "GuildPlus";
    }
    if (
      normalized === "guildmonthly" ||
      normalized === "guild" ||
      normalized === "stylefilemonthly" ||
      normalized === "stylefileannually"
    ) {
      return "Guild";
    }
    return 'basic';
  };
  
  // Use effective tier from impersonation if available, otherwise use actual subscription
  const actualUserPlan: PlanKey = mapSubscriptionToPlanKey(rawSubscription);
  const userPlan: PlanKey = effectiveTier 
    ? (effectiveTier === 'Guild+' ? 'GuildPlus' : effectiveTier === 'Basic' ? 'basic' : effectiveTier) as PlanKey
    : actualUserPlan;
  
  
  // Check if payment is required for the user's plan
  const isPaymentRequired = userPlan === 'Guild' || userPlan === 'GuildPlus';
  
  // Check if payment is completed (this would typically come from user data or localStorage)
  const isPaymentCompleted = () => {
    // For Basic plan users, they don't need payment
    if (userPlan === 'basic') {
      return true;
    }
    
    // For paid plans, check if paymentStatus is 'paid' (regardless of onboardingStatus)
    // Users who successfully paid should not see the payment gate
    if (user?.paymentStatus === 'paid') {
      return true;
    }
    
    // Also check if onboardingStatus is 'active' (means they completed everything including payment)
    if (user?.onboardingStatus === 'active') {
      return true;
    }
    
    // Fallback: check localStorage for payment completion
    const paymentStatus = localStorage.getItem(`payment_${user?.id}_${userPlan}`);
    return paymentStatus === 'completed';
  };


  // Handle payment success
  const handlePaymentSuccess = () => {
    localStorage.setItem(`payment_${user?.id}_${userPlan}`, 'completed');
    setPaymentCompleted(true);
  };


  // Check for registration success notification
  useEffect(() => {
    const registrationSuccess = sessionStorage.getItem('registrationSuccess');
    if (registrationSuccess) {
      try {
        const notification = JSON.parse(registrationSuccess);
        showSuccess(notification.message);
        sessionStorage.removeItem('registrationSuccess');
      } catch (error) {
        sessionStorage.removeItem('registrationSuccess');
      }
    }
  }, [showSuccess]);

  // Perform redirect immediately if needed (synchronously computed)
  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath);
    }
  }, [redirectPath, navigate]);

  // Show skeleton loader while loading or redirecting
  if (loading || redirectPath) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: 3, borderRadius: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton variant="rectangular" width="30%" height={150} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" width="30%" height={150} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" width="30%" height={150} sx={{ borderRadius: 2 }} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // Show admin dashboard only if effective role is admin (not when impersonating)
  if (isAdmin) {
    return <AdminDashboardPage />;
  }


  const shouldShowWelcomePopup = loading
    ? false
    : !application ||
      (application.status === DesignerApplicationSteps.CompleteApplication && !application.experience?.length);
  const shouldShowDesignerOnboardingMessage =
    !isHomeowner && shouldShowWelcomePopup;

  return (
    <>
      {isHomeowner ? (
        // For Basic and Guild plan users, show limited dashboard
        userPlan === 'basic' ? (
          <BasicPlanDashboard user={user} planKey="basic" />
        ) : userPlan === 'Guild' ? (
          !isPaymentCompleted() ? (
            <PaymentGate
              selectedPlan={userPlan}
              onPaymentSuccess={handlePaymentSuccess}
            >
              <YourHubView />
            </PaymentGate>
          ) : (
            <BasicPlanDashboard user={user} planKey="Guild" />
          )
        ) : !isPaymentCompleted() ? (
          <PaymentGate
            selectedPlan={userPlan}
            onPaymentSuccess={handlePaymentSuccess}
          >
            <YourHubView />
          </PaymentGate>
        ) : homeownerOnboardingAccess.isOnboardingComplete ? (
          <>
            <YourHubView />
          </>
        ) : (
          <>
            <WelcomeMessage
              firstName={user?.firstName ?? ""}
              status={application?.status ?? undefined}
            />
            <ApplicationStatusBlock
              currentStatus={application?.status ?? undefined}
              accountType="homeowner"
            />
          </>
        )
      ) : (
        <>
          {shouldShowWelcomePopup && <DesignerWelcomePopup />}
          <WelcomeMessage
            firstName={user?.firstName ?? ""}
            status={application?.status ?? undefined}
            message={
              shouldShowDesignerOnboardingMessage
                ? DESIGNER_ONBOARDING_WELCOME_MESSAGE
                : undefined
            }
          />
          <ApplicationStatusBlock
            currentStatus={application?.status ?? undefined}
            accountType="designer"
          />
        </>
      )}
    </>
  );
};
